import {
  type ChartDataWithPcnt,
} from '../ChartTypes';
import {
  CHART_COLORS,
  circularColor,
} from '../ChartVariables';
import { formatWithAbbreviation } from 'components/Pipes/numberPipes';
import { type FC } from 'react';
import { type TableColumnDisplayValues } from 'types';

type LegendProps = {
  currency: string,
  decimals: number,
  isInPercents: boolean,
};

export type CommitmentBarChartProps = {
  values: TableColumnDisplayValues,
};

export type ContributionBarChartProps = {
  values: TableColumnDisplayValues,
};

const renderLegend = (
  allBars: ChartDataWithPcnt[],
  colorList: string[],
  legend: LegendProps,
) => {
  return (
    <div className='flex flex-wrap content-start items-start gap-0.5'>
      {allBars.map((bar, index) =>
        <div className='grey-data-wrapper relative cursor-pointer items-center gap-x-0.25' key={bar.legendName}>
          <div
            className='size-[0.5rem] min-w-[0.5rem] rounded-[100%]'
            style={{
              background: colorList[index],
            }}
          />
          <span className='text-font-3 ms:text-font-2'>{bar.legendName}</span>
          <span className='self-start text-font-3 font-bold ms:text-font-2'>
            {`${legend.currency}${formatWithAbbreviation(
              Math.abs(bar.value),
              legend.decimals,
            )}`}
          </span>
          {legend.isInPercents ? <span className='self-start text-font-3 font-bold ms:text-font-2'>
            {`(${bar.pcnt.toFixed(1)}%)`}
          </span> : null}
          <div className='legend-tooltip'>
            <div className='tooltip-body'>
              {legend.isInPercents
                ? `${legend.currency}${formatWithAbbreviation(
                  Math.abs(bar.value),
                  legend.decimals,
                )}`
                : `${bar.pcnt.toFixed(1)}%`}
            </div>
            <div className='tooltip-tail' />
          </div>
        </div>,
      )}
    </div>
  );
};

type CombinedBarChartProps = {
  commitmentValues: CommitmentBarChartProps['values'],
  contributionValues: ContributionBarChartProps['values'],
};

export const CombinedContributionCommitmentChart: FC<CombinedBarChartProps> = ({
  commitmentValues,
  contributionValues,
}) => {
  const contributionBar = [
    {
      legendName: 'Contribution (net due/advance)',
      value: contributionValues.contributionDue + contributionValues.contribution,
    },
    {
      legendName: 'Unfunded commitment',
      value:
        contributionValues.commitment -
        contributionValues.contribution -
        contributionValues.contributionDue,
    },
  ];

  const commitmentBar = [
    { legendName: 'NAV', value: commitmentValues.nav },
    { legendName: 'Distribution', value: commitmentValues.distribution + commitmentValues.distributionWth },
  ];

  const allBars = [...contributionBar, ...commitmentBar];
  const allBarsLen = allBars.length;

  const totalContributionSum = contributionBar.reduce((sum, item) => sum + Math.abs(item.value), 0);
  const totalCommitmentSum = commitmentBar.reduce((sum, item) => sum + Math.abs(item.value), 0);

  const isContributionMax = totalContributionSum >= totalCommitmentSum;
  const maxTotal = Math.max(totalContributionSum, totalCommitmentSum);

  const contributionBarWithPcnt: ChartDataWithPcnt[] = contributionBar.map((bar) => ({
    ...bar,
    pcnt: Math.abs(bar.value) * 100 / totalContributionSum,
  }));

  const commitmentBarWithPcnt: ChartDataWithPcnt[] = commitmentBar.map((bar) => ({
    ...bar,
    pcnt: Math.abs(bar.value) * 100 / totalCommitmentSum,
  }));

  const bgListCommitment = allBars.map((_, index) =>
    circularColor(index, allBarsLen, CHART_COLORS.commitmentBarChart),
  );
  const bgListContribution = allBars.map((_, index) =>
    circularColor(index, allBarsLen, CHART_COLORS.contributionBarChart),
  );

  const contributionPercents = contributionBar.map(
    (bar) =>
      Math.abs(bar.value) * 100 * (isContributionMax ? 1 : totalContributionSum / maxTotal) /
      totalContributionSum,
  );

  const commitmentPercents = commitmentBar.map(
    (bar) =>
      Math.abs(bar.value) * 100 * (isContributionMax ? totalCommitmentSum / maxTotal : 1) /
      totalCommitmentSum,
  );

  const legendContribution: LegendProps = {
    currency: '',
    decimals: 2,
    isInPercents: true,
  };

  const legendCommitment: LegendProps = {
    currency: '',
    decimals: 2,
    isInPercents: false,
  };

  return (
    <div className='w-fill flex-1 bg-white-999'>
      <div className='flex w-full flex-col gap-[0.75rem] ms:gap-[1rem]'>
        {/* Contribution Bar */}
        <div className='flex w-full justify-start overflow-hidden rounded-sm'>
          {contributionPercents.map((pcnt, index) =>
            <div
              className='h-[1rem] last:rounded-r-sm dt:h-6'
              key={`contribution-${pcnt}`}
              style={{
                background: bgListContribution[index],
                width: `${pcnt}%`,
              }}
            />,
          )}
        </div>
        {/* Commitment Bar */}
        <div className='flex w-full justify-start overflow-hidden rounded-sm'>
          {commitmentPercents.map((pcnt, index) =>
            <div
              className='h-[1rem] last:rounded-r-sm dt:h-6'
              key={`commitment-${pcnt}`}
              style={{
                background: bgListCommitment[contributionPercents.length + index],
                width: `${pcnt}%`,
              }}
            />,
          )}
        </div>
        {/* Shared Legend */}
        <div className='flex flex-col gap-[0.5rem] ms:flex-row'>
          {renderLegend(contributionBarWithPcnt, bgListContribution, legendContribution)}
          {renderLegend(commitmentBarWithPcnt, bgListCommitment, legendCommitment)}
        </div>
      </div>
    </div>
  );
};
